import React, { useState } from "react";
import { motion } from "framer-motion";
import InteractiveBarChart from "../templates/InteractiveBarChart";

const BarChartExamples = () => {
  // Example data for different scenarios
  const [eyeColorData, setEyeColorData] = useState([
    { category: "Brown", value: 12, color: "#8B4513" },
    { category: "Blue", value: 8, color: "#6FA4FF" },
    { category: "Green", value: 5, color: "#4CAF50" },
    { category: "Hazel", value: 3, color: "#CD853F" },
  ]);

  const [fruitData, setFruitData] = useState([
    { category: "Apples", value: 7, color: "#FF8686" },
    { category: "Bananas", value: 12, color: "#FCFF6C" },
    { category: "Oranges", value: 5, color: "#FF9800" },
    { category: "Strawberries", value: 8, color: "#FF5252" },
  ]);

  const [pencilData, setPencilData] = useState([
    { category: "Red", value: 15, color: "#FF8686" },
    { category: "Blue", value: 10, color: "#6FA4FF" },
    { category: "Green", value: 8, color: "#8CFFCF" },
    { category: "Yellow", value: 6, color: "#FCFF6C" },
    { category: "Black", value: 20, color: "#222222" },
  ]);

  // State for the current example being shown
  const [currentExample, setCurrentExample] = useState("eyeColor");

  // Function to get the current data based on the example
  const getCurrentData = () => {
    switch (currentExample) {
      case "eyeColor":
        return {
          data: eyeColorData,
          setData: setEyeColorData,
          title: "Eye Colors in Our Class",
        };
      case "fruit":
        return {
          data: fruitData,
          setData: setFruitData,
          title: "Favorite Fruits",
        };
      case "pencil":
        return {
          data: pencilData,
          setData: setPencilData,
          title: "Pencil Colors in Classroom",
        };
      default:
        return {
          data: eyeColorData,
          setData: setEyeColorData,
          title: "Eye Colors in Our Class",
        };
    }
  };

  const { data, setData, title } = getCurrentData();

  // Function to add a new item to the current dataset
  const addItem = () => {
    const newColors = ["#9C27B0", "#3F51B5", "#009688", "#FF5722", "#795548"];
    const newCategories = {
      eyeColor: ["Gray", "Amber", "Black", "Other"],
      fruit: ["Grapes", "Kiwi", "Mango", "Pineapple"],
      pencil: ["Purple", "Orange", "Pink", "Brown"],
    };

    // Select a random category and color
    const categories = newCategories[currentExample];
    const availableCategories = categories.filter(
      (category) => !data.find((item) => item.category === category)
    );

    if (availableCategories.length === 0) return; // No more categories to add

    const randomCategory =
      availableCategories[
        Math.floor(Math.random() * availableCategories.length)
      ];
    const randomColor = newColors[Math.floor(Math.random() * newColors.length)];
    const randomValue = Math.floor(Math.random() * 15) + 1; // Random value between 1-15

    // Add the new item to the data
    setData([
      ...data,
      { category: randomCategory, value: randomValue, color: randomColor },
    ]);
  };

  // Function to remove the last item from the current dataset
  const removeItem = () => {
    if (data.length <= 1) return; // Keep at least one item
    setData(data.slice(0, -1));
  };

  // Function to update random values in the dataset
  const updateValues = () => {
    const newData = data.map((item) => ({
      ...item,
      value: Math.floor(Math.random() * 20) + 1, // Random value between 1-20
    }));
    setData(newData);
  };

  return (
    <div className="space-y-4">
      {/* Example selector */}
      <div className="flex justify-center flex-wrap gap-2">
        <button
          onClick={() => setCurrentExample("eyeColor")}
          className={`px-3 py-1.5 rounded-lg text-sm ${
            currentExample === "eyeColor"
              ? "bg-[#5F29FF] text-white"
              : "bg-white text-[#0D065B] hover:bg-gray-100"
          }`}
        >
          Eye Colors
        </button>
        <button
          onClick={() => setCurrentExample("fruit")}
          className={`px-3 py-1.5 rounded-lg text-sm ${
            currentExample === "fruit"
              ? "bg-[#5F29FF] text-white"
              : "bg-white text-[#0D065B] hover:bg-gray-100"
          }`}
        >
          Favorite Fruits
        </button>
        <button
          onClick={() => setCurrentExample("pencil")}
          className={`px-3 py-1.5 rounded-lg text-sm ${
            currentExample === "pencil"
              ? "bg-[#5F29FF] text-white"
              : "bg-white text-[#0D065B] hover:bg-gray-100"
          }`}
        >
          Pencil Colors
        </button>
      </div>

      {/* Interactive chart */}
      <motion.div
        key={currentExample}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white p-4 rounded-xl shadow-sm"
      >
        <InteractiveBarChart
          initialData={data}
          title={title}
          onDataChange={setData}
          height={220} // Reduced height for better fit
        />

        {/* Instructions */}
        <div className="mt-3 text-center text-xs text-[#5F29FF]">
          <p>
            Click and drag a bar to change its height, or click on a bar and
            enter a specific number.
          </p>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex justify-center flex-wrap gap-2">
          <button
            onClick={addItem}
            className="px-3 py-1.5 bg-[#D6DCFF] text-[#0D065B] rounded-lg hover:bg-[#C6CCFF] flex items-center text-sm"
          >
            <span className="mr-1">➕</span> Add Category
          </button>
          <button
            onClick={removeItem}
            className="px-3 py-1.5 bg-[#FFDFDF] text-[#0D065B] rounded-lg hover:bg-[#FFCFCF] flex items-center text-sm"
            disabled={data.length <= 1}
          >
            <span className="mr-1">➖</span> Remove Category
          </button>
          <button
            onClick={updateValues}
            className="px-3 py-1.5 bg-[#F1FFF9] text-[#0D065B] rounded-lg hover:bg-[#E1FFF0] flex items-center text-sm"
          >
            <span className="mr-1">🔄</span> Randomize
          </button>
        </div>
      </motion.div>

      {/* Tips section - condensed */}
      <div className="bg-[#EFEAFC] p-3 rounded-xl">
        <h3 className="text-base font-medium text-[#0D065B] mb-2">
          Chart Tips:
        </h3>
        <ul className="space-y-1 text-sm">
          <li className="flex items-center">
            <span className="text-lg mr-2">📏</span>
            <span>Notice how the scale automatically adjusts!</span>
          </li>
          <li className="flex items-center">
            <span className="text-lg mr-2">🎨</span>
            <span>Colors make categories easier to distinguish.</span>
          </li>
          <li className="flex items-center">
            <span className="text-lg mr-2">👀</span>
            <span>Compare heights to see which has more/less.</span>
          </li>
        </ul>
      </div>

      {/* What you learned section - condensed */}
      <div className="bg-[#D6DCFF] p-3 rounded-xl">
        <h3 className="text-base font-medium text-[#0D065B] mb-2">
          What This Shows:
        </h3>
        <ul className="space-y-1 text-sm">
          <li className="flex items-center">
            <span className="text-lg mr-2">📊</span>
            <span>More categories = wider chart with more bars</span>
          </li>
          <li className="flex items-center">
            <span className="text-lg mr-2">📈</span>
            <span>Larger values = taller bars</span>
          </li>
          <li className="flex items-center">
            <span className="text-lg mr-2">🔍</span>
            <span>Charts help spot patterns at a glance!</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default BarChartExamples;
